import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Autocomplete,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import axios from "axios";
import NotificationSnackbar from "./NotificationBar";

function AssignPayrollGroup({ open, onClose, payrollGroup, onSaveSuccess }) {
  const theme = useTheme();

  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedEntitySector, setSelectedEntitySector] = useState(null);

  const [loadingEntities, setLoadingEntities] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [errors, setErrors] = useState({});

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Fetch entities when modal opens
  useEffect(() => {
    if (!open) return;

    const fetchEntities = async () => {
      try {
        setLoadingEntities(true);

        const response = await axios.get("/api/support/entities");

        const data = response.data;

        setEntities(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching entities:", error);

        setSnackbarMessage("Failed to load entities");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoadingEntities(false);
      }
    };

    fetchEntities();
  }, [open]);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedEntity(null);
      setSelectedEntitySector(null);
      setErrors({});
      setSaved(false);
    }
  }, [open]);

  // Handle Entity Change
  const handleEntityChange = (event, entity) => {
    setSelectedEntity(entity);

    // Reset entity sector whenever entity changes
    setSelectedEntitySector(null);

    setErrors((prev) => ({
      ...prev,
      entity: "",
      entitySector: "",
    }));
  };

  // Handle Entity Sector Change
  const handleEntitySectorChange = (event, entitySector) => {
    setSelectedEntitySector(entitySector);

    setErrors((prev) => ({
      ...prev,
      entitySector: "",
    }));
  };

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!selectedEntity) {
      newErrors.entity = "Entity is required";
    }

    if (!selectedEntitySector) {
      newErrors.entitySector = "Entity sector is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    if (!payrollGroup?.id) {
      setSnackbarMessage("Payroll Group ID is missing");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setSaving(true);

    try {
      const response = await axios.post("/api/support/assign-payroll-group", {
        entitySectorId: selectedEntitySector.entitySectorId,
        payrollGroupId: payrollGroup.id,
      });

      setSnackbarMessage(
        response.data?.message || "Payroll Group assigned successfully",
      );

      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setSaved(true);

      setTimeout(() => {
        setSaved(false);
        onSaveSuccess?.();
        onClose();
      }, 1000);
    } catch (error) {
      console.error("Payroll Group assignment error:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to assign Payroll Group";

      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  // Close modal
  const handleClose = () => {
    if (saving) return;

    setSelectedEntity(null);
    setSelectedEntitySector(null);
    setErrors({});
    setSaved(false);

    onClose();
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: alpha(theme.palette.primary.light, 0.08),

      "& fieldset": {
        borderColor: alpha(theme.palette.primary.light, 0.5),
      },

      "&:hover fieldset": {
        borderColor: theme.palette.primary.light,
      },

      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      sx={{
        backgroundColor: "rgba(0, 0, 0, 0.7)",
      }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "visible",
          border: `1px solid ${alpha(theme.palette.primary.light, 0.5)}`,
        },
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          bgcolor: theme.palette.primary.light,
          px: 2.5,
          pt: 2.5,
          pb: 2,
          borderRadius: "12px 12px 0 0",
        }}
      >
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AssignmentIcon
                sx={{
                  fontSize: 18,
                  color: "#fff",
                }}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 500,
                  color: "#fff",
                  lineHeight: 1.3,
                }}
              >
                Assign Payroll Group
              </Typography>

              <Typography
                sx={{
                  fontSize: 12,
                  color: "rgba(255,255,255,0.7)",
                  mt: 0.3,
                }}
              >
                Assign payroll group to an entity sector
              </Typography>
            </Box>
          </Box>

          <IconButton
            size="small"
            onClick={handleClose}
            disabled={saving}
            sx={{
              color: "rgba(255,255,255,0.8)",
              border: "1px solid rgba(255,255,255,0.3)",

              "&:hover": {
                bgcolor: "rgba(255,255,255,0.15)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {/* CURRENT PAYROLL GROUP */}
        {payrollGroup?.id && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              mt: 1.5,
              px: 1.5,
              py: 0.75,
              borderRadius: "20px",
              bgcolor: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "#fff",
              }}
            />

            <Typography
              sx={{
                fontSize: 11,
                color: "rgba(255,255,255,0.65)",
              }}
            >
              Payroll Group:
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 500,
                color: "#fff",
              }}
            >
              {payrollGroup.name || payrollGroup.PayrollGroup}
            </Typography>
          </Box>
        )}
      </Box>

      {/* FORM */}
      <DialogContent
        sx={{
          pt: 2.5,
          pb: 1,
          bgcolor: "#fff",
          overflow: "visible",
        }}
      >
        <Box display="flex" flexDirection="column" gap={1.5}>
          {/* ENTITY */}
          <Autocomplete
            options={entities}
            value={selectedEntity}
            onChange={handleEntityChange}
            loading={loadingEntities}
            disabled={saving}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) =>
              String(option.id) === String(value.id)
            }
            noOptionsText="No entities found"
            clearOnEscape
            disablePortal
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                size="small"
                label="Entity"
                placeholder="Search entity..."
                error={!!errors.entity}
                helperText={errors.entity}
                sx={fieldSx}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingEntities && (
                        <CircularProgress color="inherit" size={18} />
                      )}

                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* ENTITY SECTOR */}
          <Autocomplete
            options={selectedEntity?.entitySectors || []}
            value={selectedEntitySector}
            onChange={handleEntitySectorChange}
            disabled={!selectedEntity || saving}
            getOptionLabel={(option) =>
              `${selectedEntity?.name || ""} (${option.sectorName || ""})`
            }
            isOptionEqualToValue={(option, value) =>
              String(option.entitySectorId) === String(value.entitySectorId)
            }
            noOptionsText={
              selectedEntity
                ? "No sectors found for this entity"
                : "Select an entity first"
            }
            clearOnEscape
            disablePortal
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                key={option.entitySectorId}
                sx={{
                  display: "flex",
                  gap: 0.5,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    color: theme.palette.text.secondary,
                  }}
                >
                  {selectedEntity?.name}
                </Typography>

                <Typography
                  component="span"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                  }}
                >
                  ({option.sectorName})
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                size="small"
                label="Entity Sector"
                placeholder="Search entity sector..."
                error={!!errors.entitySector}
                helperText={errors.entitySector}
                sx={fieldSx}
              />
            )}
          />
        </Box>
      </DialogContent>

      {/* FOOTER LINE */}
      <Box
        sx={{
          height: "1px",
          bgcolor: alpha(theme.palette.primary.light, 0.4),
        }}
      />

      {/* FOOTER */}
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.75,
          gap: 1,
          bgcolor: alpha(theme.palette.primary.light, 0.08),
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          size="small"
          disabled={saving}
          sx={{
            textTransform: "none",
            borderColor: alpha(theme.palette.primary.light, 0.5),
            color: "text.secondary",

            "&:hover": {
              bgcolor: alpha(theme.palette.primary.light, 0.1),
              borderColor: theme.palette.primary.light,
            },
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={
            saving ||
            saved ||
            !selectedEntity ||
            !selectedEntitySector ||
            !payrollGroup?.id
          }
          startIcon={
            saved ? (
              <CheckIcon sx={{ fontSize: 14 }} />
            ) : saving ? (
              <CircularProgress size={13} color="inherit" />
            ) : (
              <AssignmentIcon sx={{ fontSize: 14 }} />
            )
          }
          sx={{
            textTransform: "none",
            minWidth: 115,

            bgcolor: saved
              ? theme.palette.primary.main
              : theme.palette.primary.light,

            "&:hover": {
              bgcolor: theme.palette.primary.main,
            },

            "&.Mui-disabled": {
              bgcolor: alpha(theme.palette.primary.light, 0.4),
            },
          }}
        >
          {saved ? "Assigned!" : saving ? "Assigning..." : "Assign"}
        </Button>
      </DialogActions>

      {/* SNACKBAR */}
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Dialog>
  );
}

export default AssignPayrollGroup;
